import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, Search } from 'lucide-react';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { DatePicker } from '@/components/ui/date-picker';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { FindBar } from '@/components/find-bar';
import { HighlightTextarea } from '@/components/highlight-textarea';
import { useInPageFind } from '@/lib/use-in-page-find';
import { cn } from '@/lib/utils';
import { useCreateArticle, useFonti, useUpdateArticle } from '@/lib/queries';
import type { ArticleInput } from '@shared/schemas';

type Props = {
  initial?: Partial<ArticleInput>;
  articleId?: string;
  /** Query "trova" riportata dalla vista (?find=) per evidenziare in edit. */
  initialFind?: string;
};

/**
 * Porta in vista, dentro la textarea, l'occorrenza che inizia a `index`,
 * misurando l'offset verticale con un div "specchio" (gestisce il word-wrap).
 */
function scrollTextareaToOffset(ta: HTMLTextAreaElement, index: number) {
  const style = window.getComputedStyle(ta);
  const mirror = document.createElement('div');
  const copy = [
    'boxSizing', 'paddingTop', 'paddingRight', 'paddingBottom', 'paddingLeft',
    'borderTopWidth', 'borderRightWidth', 'borderBottomWidth', 'borderLeftWidth',
    'fontStyle', 'fontVariant', 'fontWeight', 'fontSize', 'fontFamily',
    'lineHeight', 'letterSpacing', 'wordSpacing', 'textTransform', 'tabSize',
  ] as const;
  for (const p of copy) {
    mirror.style[p as never] = style[p as never];
  }
  mirror.style.position = 'absolute';
  mirror.style.top = '-9999px';
  mirror.style.left = '-9999px';
  mirror.style.height = 'auto';
  mirror.style.overflow = 'hidden';
  mirror.style.whiteSpace = 'pre-wrap';
  mirror.style.wordWrap = 'break-word';
  mirror.style.width = `${ta.clientWidth}px`;

  const marker = document.createElement('span');
  mirror.textContent = ta.value.slice(0, index);
  marker.textContent = ta.value.slice(index, index + 1) || '.';
  mirror.appendChild(marker);
  document.body.appendChild(mirror);
  const top = marker.offsetTop;
  document.body.removeChild(mirror);

  ta.scrollTop = Math.max(0, top - ta.clientHeight / 2);
}

export function ArticleForm({ initial, articleId, initialFind }: Props) {
  const navigate = useNavigate();
  const create = useCreateArticle();
  const update = useUpdateArticle();
  const fonti = useFonti();
  const [titolo, setTitolo] = useState(initial?.titolo ?? '');
  const [data, setData] = useState(initial?.data ?? '');
  const [fonte, setFonte] = useState(initial?.fonte ?? '');
  const [contenuto, setContenuto] = useState(initial?.contenuto ?? '');
  const [error, setError] = useState<string | null>(null);
  const [justSaved, setJustSaved] = useState(false);
  const savedTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Stessa lista del filtro Fonte della home (useFonti). La fonte corrente, se
  // assente dall'elenco (es. articolo vecchio), viene aggiunta in testa così
  // la modifica non perde il valore esistente.
  const fonteOptions = useMemo(() => {
    const list = fonti.data?.fonti ?? [];
    return fonte && !list.includes(fonte) ? [fonte, ...list] : list;
  }, [fonti.data, fonte]);

  // Ricerca "trova nel testo": segmenti = [titolo, contenuto] (in ordine).
  const segments = useMemo(() => [titolo, contenuto], [titolo, contenuto]);
  const find = useInPageFind(segments, { initialQuery: initialFind });
  const { setQuery, setOpen } = find;
  const titoloRef = useRef<HTMLTextAreaElement>(null);
  const contenutoRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    return () => {
      if (savedTimer.current) clearTimeout(savedTimer.current);
    };
  }, []);

  // Porta in vista l'occorrenza corrente scorrendo la textarea (il backdrop si
  // sincronizza da solo). Si attiva su navigazione (currentIndex) o cambio
  // query, MAI mentre si edita la stessa textarea (guardia activeElement).
  useEffect(() => {
    if (!find.open) return;
    const m = find.currentMatch;
    if (!m) return;
    const ta = m.segmentIndex === 0 ? titoloRef.current : contenutoRef.current;
    if (!ta || document.activeElement === ta) return;
    scrollTextareaToOffset(ta, m.start);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [find.currentIndex, find.query, find.open]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);

    if (!/^\d{4}-\d{2}-\d{2}$/.test(data)) {
      setError('Data richiesta in formato YYYY-MM-DD');
      return;
    }
    if (!fonte.trim()) {
      setError('Fonte richiesta');
      return;
    }
    const [yStr, mStr] = data.split('-');
    const anno = Number(yStr);
    const mese = Number(mStr);

    const payload: ArticleInput = {
      anno,
      mese,
      data,
      fonte: fonte.trim(),
      titolo: titolo.trim(),
      contenuto: contenuto.trim(),
    };

    try {
      if (articleId) {
        await update.mutateAsync({ id: articleId, input: payload });
        setJustSaved(true);
        if (savedTimer.current) clearTimeout(savedTimer.current);
        savedTimer.current = setTimeout(() => setJustSaved(false), 2500);
      } else {
        await create.mutateAsync(payload);
        navigate('/admin/articoli');
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Errore salvataggio');
    }
  }

  const pending = create.isPending || update.isPending;

  const inputCls =
    'rounded-md border border-(--color-input) bg-(--color-background) px-3 py-2 text-base focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-(--color-ring)';

  return (
    <form onSubmit={onSubmit} className="space-y-6">
      {/* Toolbar di ricerca, allineata alla colonna del form */}
      <div className="flex min-h-11 items-center justify-end">
        {find.open ? (
          <FindBar
            ref={find.inputRef}
            query={find.query}
            onQueryChange={setQuery}
            total={find.total}
            current={find.total === 0 ? 0 : find.currentIndex + 1}
            onPrev={find.prev}
            onNext={find.next}
            onClose={find.close}
            className="w-full max-w-md"
          />
        ) : (
          <Button type="button" variant="outline" size="sm" onClick={() => setOpen(true)}>
            <Search className="h-4 w-4" /> Cerca nel testo
          </Button>
        )}
      </div>

      <div className="grid gap-5 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label htmlFor="data">Data</Label>
          <DatePicker id="data" required value={data} onChange={setData} placeholder="Seleziona data" />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="fonte">Fonte</Label>
          <Select value={fonte} onValueChange={setFonte}>
            <SelectTrigger id="fonte" className="w-full">
              <SelectValue placeholder="Seleziona fonte" />
            </SelectTrigger>
            <SelectContent>
              {fonteOptions.map((f) => (
                <SelectItem key={f} value={f}>
                  {f}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="titolo">Titolo</Label>
        <HighlightTextarea
          id="titolo"
          ref={titoloRef}
          required
          rows={2}
          maxLength={2000}
          className={cn(inputCls, 'resize-y leading-snug')}
          value={titolo}
          onChange={setTitolo}
          matches={find.matches.filter((m) => m.segmentIndex === 0)}
          currentIndex={find.currentIndex}
        />
      </div>

      <div className="space-y-1.5">
        <div className="flex items-baseline justify-between">
          <Label htmlFor="contenuto">Contenuto</Label>
          <span className="text-xs tabular-nums text-(--color-muted-foreground)">
            {contenuto.length.toLocaleString('it-IT')} caratteri
          </span>
        </div>
        <HighlightTextarea
          id="contenuto"
          ref={contenutoRef}
          required
          rows={22}
          className={cn(inputCls, 'resize-y font-serif leading-relaxed')}
          value={contenuto}
          onChange={setContenuto}
          matches={find.matches.filter((m) => m.segmentIndex === 1)}
          currentIndex={find.currentIndex}
        />
      </div>

      {error && (
        <p className="text-sm text-(--color-destructive)" role="alert">
          {error}
        </p>
      )}

      <div className="flex items-center justify-end gap-2 border-t border-(--color-border) pt-5">
        <Button type="button" variant="outline" onClick={() => navigate(-1)}>
          Annulla
        </Button>
        <Button type="submit" disabled={pending}>
          {pending ? (
            'Salvataggio…'
          ) : justSaved && articleId ? (
            <>
              <Check className="h-4 w-4" /> Salvato
            </>
          ) : articleId ? (
            'Salva modifiche'
          ) : (
            'Crea articolo'
          )}
        </Button>
      </div>
    </form>
  );
}
